// What the repertoire says a guitarist should learn FIRST (Sep 2026, the Learn
// "Where to start" page): the corpus stats reshaped into a syllabus. Pure —
// the page passes in `CorpusStats` from the Jazz 1460 and draws what comes
// back — and tested against synthetic charts, so the ordering rules hold
// whatever the bundled list says. Three questions:
//
//  1. Which chord TYPES carry the tunes? Shares and the running total, so the
//     page can say "these six are 85% of every chord".
//  2. Which of those are the SAME SHAPE? Synonyms (synonyms.ts): a ninth chord
//     drops its root, so Cmaj9 is literally Em7, and C6 is Am7 — one grip
//     learned, several symbols covered. The families are unions over the
//     synonym relation; the shape to learn is the most common member.
//  3. Which PROGRESSIONS recur? The corpus's roman bigrams and trigrams,
//     parsed back into concrete chords in C so the page can voice them.

import { CHORD_TYPES, chordSymbol, type ChordTypeId } from '@/music/chords';
import type { CorpusStats, RankedCount } from '@/music/corpus';
import type { ProgressionStep } from '@/music/progressions';
import { findSynonyms } from '@/music/synonyms';

export interface TypeShare {
  type: ChordTypeId;
  /** The symbol on C, the way the page names the type ("Cm7"). */
  symbol: string;
  count: number;
  /** Share of every chord in the corpus, 0..1. */
  share: number;
  /** Running total down the list, 0..1. */
  cumulative: number;
}

export function chordTypeShares(stats: CorpusStats, limit = 12): TypeShare[] {
  const total = Math.max(1, stats.totalChords);
  let cumulative = 0;
  return stats.qualities
    .filter((q): q is RankedCount & { label: ChordTypeId } => q.label in CHORD_TYPES)
    .slice(0, limit)
    .map((q) => {
      cumulative += q.count;
      return {
        type: q.label,
        symbol: chordSymbol('C', q.label),
        count: q.count,
        share: q.count / total,
        cumulative: cumulative / total,
      };
    });
}

export interface ShapeFamily {
  /** The type to learn: the most common member. */
  shape: TypeShare;
  /** The other types the same grips answer, each with one worked identity. */
  covers: { type: TypeShare; example: string }[];
  /** Share of the corpus the whole family accounts for. */
  share: number;
}

const FLAT_NAMES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

/**
 * Group the listed types by shape. Two types are one shape when some root of
 * one voices exactly the pitch-class set of the other (Am7 = C6 = F∆9); the
 * relation is symmetric, and the transitive closure is what a hand learns.
 * Symmetric chords (°7) close over their own transpositions and so form a
 * family of one type — which is still the point: one shape, four names.
 */
export function shapeFamilies(types: TypeShare[]): ShapeFamily[] {
  const byType = new Map(types.map((t) => [t.type, t]));
  const parent = new Map<ChordTypeId, ChordTypeId>(types.map((t) => [t.type, t.type]));
  const find = (t: ChordTypeId): ChordTypeId => {
    let r = t;
    while (parent.get(r) !== r) r = parent.get(r)!;
    return r;
  };
  const union = (a: ChordTypeId, b: ChordTypeId) => parent.set(find(a), find(b));
  // The identity that joins two types, remembered for the worked example.
  const identity = new Map<string, string>();
  for (const t of types) {
    for (const other of findSynonyms(0, t.type)) {
      if (!byType.has(other.typeId) || other.typeId === t.type) continue;
      union(t.type, other.typeId);
      const key = [t.type, other.typeId].sort().join('|');
      if (!identity.has(key)) {
        identity.set(
          key,
          `${chordSymbol('C', t.type)} = ${chordSymbol(FLAT_NAMES[other.rootPc], other.typeId)}`,
        );
      }
    }
  }
  const groups = new Map<ChordTypeId, TypeShare[]>();
  for (const t of types) {
    const root = find(t.type);
    groups.set(root, [...(groups.get(root) ?? []), t]);
  }
  return [...groups.values()]
    .map((members) => {
      const sorted = [...members].sort((a, b) => b.count - a.count);
      const shape = sorted[0];
      const covers = sorted.slice(1).map((type) => ({
        type,
        example:
          identity.get([shape.type, type.type].sort().join('|')) ??
          // Joined through a third member: name the type's own C identity.
          `${chordSymbol('C', type.type)} = ${(() => {
            const via = findSynonyms(0, type.type).find((s) => byType.has(s.typeId));
            return via
              ? chordSymbol(FLAT_NAMES[via.rootPc], via.typeId)
              : chordSymbol('C', shape.type);
          })()}`,
      }));
      return {
        shape,
        covers,
        share: members.reduce((sum, m) => sum + m.share, 0),
      };
    })
    .sort((a, b) => b.share - a.share);
}

export interface CommonProgression {
  /** The corpus label, "IIm → V7 → I∆". */
  label: string;
  count: number;
  /** Share of all transitions of that length. */
  steps: ProgressionStep[];
}

const DEGREES = ['I', '♭II', 'II', '♭III', 'III', 'IV', '♭V', 'V', '♭VI', 'VI', '♭VII', 'VII'];
const FAMILY_TYPE: Record<string, ChordTypeId> = {
  '∆': 'maj7',
  m: 'min7',
  '7': 'dom7',
  ø: 'min7b5',
  '°': 'dim7',
};

/** "♭VII7" → the chord in C, or null for anything the corpus labels oddly. */
export function romanToStep(roman: string): ProgressionStep | null {
  const m = /^(♭?[IV]+)(∆|m|7|ø|°)$/.exec(roman.trim());
  if (!m) return null;
  const degree = DEGREES.indexOf(m[1]);
  if (degree < 0) return null;
  return { root: FLAT_NAMES[degree], type: FAMILY_TYPE[m[2]] };
}

/**
 * The corpus's most common two- and three-chord moves as concrete steps in C.
 * A move that only restates one chord (I∆ → I∆ is a held bar, not a
 * progression) is left out, as is any label the parser cannot read.
 */
export function commonProgressions(rows: RankedCount[], limit = 6): CommonProgression[] {
  const out: CommonProgression[] = [];
  for (const row of rows) {
    const romans = row.label.split('→').map((s) => s.trim());
    const steps = romans.map(romanToStep);
    if (steps.some((s) => s === null)) continue;
    const concrete = steps as ProgressionStep[];
    const distinct = new Set(concrete.map((s) => `${s.root}${s.type}`));
    if (distinct.size < concrete.length) continue;
    out.push({ label: row.label, count: row.count, steps: concrete });
    if (out.length >= limit) break;
  }
  return out;
}
